import {Trans} from '@lingui/macro';
import {noop} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUnmountCancelToken} from '../../../../../../srcCommon/helpers/hookHelpers';
import {OkCancelDialog} from '../../../../../components/common/dialogs/okCancelDialog';
import {FolderBreadcrumb} from '../../../../../components/folderBreadcrumbs/folderBreadcrumbs';
import {CancelError, CancelToken} from '../../../../../helpers/asyncCancelHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {CompanyModel} from '../../../../../models/api/companyModel';
import {ApiRequiredTagging, serializeRequiredTagging} from '../../../../../models/api/requiredTaggingModel';
import {RuleBundleModel} from '../../../../../models/api/ruleBundleModel';
import {ApiSafetyNets, SafetyNetModel} from '../../../../../models/api/safetyNetModel';
import {deleteAsync, fetchApiAsync, patchApiAsync, postApiAsync} from '../../../../../services/http/http';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsWrapper,
} from '../../../../common/settingsWrapper';
import {CustomFields} from '../../../../editors/commons/customFieldsModel';
import {
  createRuleEditorContext,
  RuleEditorContextProvider,
} from '../../../../editors/ruleEditor/ruleEditorContext';
import {getSettingsType, parseApiError} from '../../../ruleHelpers';
import {RuleIntegration} from '../../../ruleIntegrationModel';
import {RuleComplexRecipeTypesEnum} from '../../../ruleRecipeModel';
import {DeleteDialog} from '../../dialog/deleteDialog';
import {StyledRuleEditorWrapper, useRuleEditorErrorBanner} from '../../ruleEditor';
import {RuleEditorFooter} from '../../ruleEditorFooter';
import {RuleEditorHeader} from '../../ruleEditorHeader';
import {RuleEditorRuleNameInput} from '../../ruleEditorRuleNameInput';
import {useRequiredTaggingEditorState} from './hooks/useRequiredTaggingEditorState';
import {RequiredTaggingEditorActiveTooltip} from './requiredTaggingEditorActiveTooltip';
import {RequiredTaggingEditorForm} from './requiredTaggingEditorForm';
import {RequiredTaggingEditorSidebar} from './requiredTaggingEditorSidebar';
import {getRequiredTaggingErrors, requiredTaggingToBundle} from './state/requiredTaggingEditorState';

/*
 * Props.
 */

interface RequiredTaggingEditorProps {
  namespace: string;
  ruleIntegration: RuleIntegration;
  customFields: CustomFields;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  navigateToRules: Handler;
  ruleBundle?: RuleBundleModel;

  // Feature flags.
  hasHighlightErrorsFlag: boolean;
}

/*
 * Component.
 */

export const RequiredTaggingEditor: FC<RequiredTaggingEditorProps> = (props) => {
  const {namespace, ruleIntegration, customFields, navigateToRules, ruleBundle, hasHighlightErrorsFlag} =
    props;

  const company = useAppSelector(getCompany);
  const {setErrorBannerMessage, maybeRenderErrorBanner} = useRuleEditorErrorBanner();
  const [shouldShowDeleteDialog, setShouldShowDeleteDialog] = useState(false);
  const [shouldShowEditDialog, setShouldShowEditDialog] = useState(false);
  const [shouldShowCreateDialog, setShouldShowCreateDialog] = useState(false);
  const [isOkDisabled, setIsOkDisabled] = useState(false);

  const safetyNets = useSafetyNets(company, namespace, ruleBundle, setErrorBannerMessage);

  const editorContext = useMemo(
    () =>
      createRuleEditorContext({
        namespace,
        hasExpressions: false,
        ruleIntegration,
        isLibrary: true,
        hasHighlightErrorsFlag,
        expressions: [],
        customFields,
      }),
    [namespace, ruleIntegration, hasHighlightErrorsFlag, customFields],
  );

  const {requiredTaggingEditorState, ...actions} = useRequiredTaggingEditorState(
    ruleBundle,
    customFields,
    ruleIntegration,
    namespace,
  );

  const onCreateRequiredTagging = async () => {
    const requiredTaggingRule = requiredTaggingToBundle(requiredTaggingEditorState);
    const url = `${company.url}/namespaces/${namespace}/required_tagging`;

    try {
      const created = await postApiAsync(
        ApiRequiredTagging,
        url,
        serializeRequiredTagging(requiredTaggingRule),
      );

      trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_CREATE, {
        rule_id: created.id,
        recipe_id: RuleComplexRecipeTypesEnum.REQUIRED_TAGGING,
        settings_page: 'rules',
        settings_type: getSettingsType(namespace),
      });
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
      return;
    }

    navigateToRules();
  };

  const onEditRequiredTagging = async () => {
    if (!ruleBundle?.id) {
      return;
    }

    const requiredTaggingRule = requiredTaggingToBundle(requiredTaggingEditorState);
    const url = `${company.url}/namespaces/${namespace}/required_tagging/${ruleBundle.id}`;
    setIsOkDisabled(true);

    try {
      await patchApiAsync(ApiRequiredTagging, url, serializeRequiredTagging(requiredTaggingRule));
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
      return;
    } finally {
      setIsOkDisabled(false);
    }

    navigateToRules();
  };

  const onDeleteRequiredTagging = async () => {
    if (!ruleBundle || !ruleBundle.id) {
      return;
    }

    try {
      await deleteAsync(`${company.url}/namespaces/${namespace}/required_tagging/${ruleBundle.id}`);
    } catch (err) {
      setErrorBannerMessage(parseApiError(err));
      return;
    }

    navigateToRules();
  };

  const onEdit = () => setShouldShowEditDialog(true);
  const onDelete = () => setShouldShowDeleteDialog(true);
  const onCreate = () => setShouldShowCreateDialog(true);

  const errors = getRequiredTaggingErrors(
    requiredTaggingEditorState,
    editorContext.ruleIntegration,
    safetyNets,
    editorContext.hasHighlightErrorsFlag,
  );

  const maybeRenderDeleteDialog = () => {
    if (!shouldShowDeleteDialog) {
      return null;
    }

    return <DeleteDialog onOk={onDeleteRequiredTagging} onCancel={() => setShouldShowDeleteDialog(false)} />;
  };

  const maybeRenderEditDialog = () => {
    if (!shouldShowEditDialog) {
      return null;
    }

    return (
      <OkCancelDialog
        title={<Trans id="required-tagging-editor-dialog-edit-title">Edit 1 rule</Trans>}
        isOkDisabled={isOkDisabled}
        onOkClick={onEditRequiredTagging}
        onRequestClose={() => setShouldShowEditDialog(false)}
        okButtonLabel={<Trans id="required-tagging-editor-dialog-edit-confirm">Edit rule</Trans>}
      >
        <Trans id="required-tagging-editor-dialog-edit-message">
          This update will only impact conversations moving forward, not retroactively.
        </Trans>
      </OkCancelDialog>
    );
  };

  const maybeRenderCreateDialog = () => {
    if (!shouldShowCreateDialog) {
      return null;
    }

    return (
      <OkCancelDialog
        title={<Trans id="required-tagging-editor-dialog-create-title">Create 1 rule</Trans>}
        onOkClick={onCreateRequiredTagging}
        onRequestClose={() => setShouldShowCreateDialog(false)}
        okButtonLabel={<Trans id="required-tagging-editor-dialog-create-confirm">Create rule</Trans>}
      >
        <Trans id="required-tagging-editor-dialog-create-message">
          This rule will only impact conversations moving forward, not retroactively.
        </Trans>
      </OkCancelDialog>
    );
  };

  return (
    <RuleEditorContextProvider context={editorContext}>
      <StyledSettingsWrapper>
        <RuleEditorHeader
          isActive
          isActiveReadonly
          namespace={namespace}
          isEditing={Boolean(ruleBundle)}
          isReadonly={editorContext.isReadOnly}
          title={<Trans id="required-tagging-editor-title">Required tagging rule</Trans>}
          breadcrumbs={props.breadcrumbs}
          // TODO @required-tagging: Handle enable/disable.
          onUpdateIsActive={noop}
          renderActiveTooltip={renderRequiredTaggingActiveTooltip}
          onDelete={onDelete}
        />
        <StyledSettingsMain>
          <StyledRuleEditorWrapper>
            <StyledSettingsContent>
              <RuleEditorRuleNameInput
                ruleName={requiredTaggingEditorState.ruleState.name}
                onUpdateRuleName={actions.onUpdateRuleName}
              />
              <RequiredTaggingEditorForm
                isEditing={Boolean(ruleBundle)}
                state={requiredTaggingEditorState}
                actions={actions}
                safetyNets={safetyNets}
              />
              {maybeRenderErrorBanner()}
              {maybeRenderDeleteDialog()}
              {maybeRenderEditDialog()}
              {maybeRenderCreateDialog()}
            </StyledSettingsContent>
          </StyledRuleEditorWrapper>
          <RequiredTaggingEditorSidebar />
        </StyledSettingsMain>
        <RuleEditorFooter
          editorErrors={errors}
          stateErrors={requiredTaggingEditorState.errors}
          isEditing={Boolean(ruleBundle)}
          onCreate={onCreate}
          onEdit={onEdit}
          onCancel={navigateToRules}
        />
      </StyledSettingsWrapper>
    </RuleEditorContextProvider>
  );
};

/*
 * Helpers.
 */

function useSafetyNets(
  company: CompanyModel,
  namespace: string,
  ruleBundle: RuleBundleModel | undefined,
  setErrorBannerMessage: HandlerOf<React.ReactNode>,
) {
  const [safetyNets, setSafetyNets] = useState<ReadonlyArray<SafetyNetModel>>([]);

  const fetchSafetyNetsAsync = useCallback(
    async (cancelToken: CancelToken) => {
      try {
        const fetchedSafetyNets = await fetchApiAsync(
          ApiSafetyNets,
          `${company.url}/namespaces/${namespace}/safety_nets`,
          {cancelToken},
        );

        // No rule bundle -> not editing
        if (!ruleBundle) {
          setSafetyNets(fetchedSafetyNets);
          return;
        }

        const otherSafetyNets = fetchedSafetyNets.filter((net) => net.ownerId !== ruleBundle.id);
        setSafetyNets(otherSafetyNets);
      } catch (err) {
        if (err instanceof CancelError) {
          return;
        }

        setErrorBannerMessage(parseApiError(err));
      }
    },
    [company.url, namespace, ruleBundle, setErrorBannerMessage],
  );

  const cancelToken = useUnmountCancelToken();
  useEffect(() => {
    fetchSafetyNetsAsync(cancelToken);
  }, [fetchSafetyNetsAsync, cancelToken]);

  return safetyNets;
}

export function renderRequiredTaggingActiveTooltip() {
  return <RequiredTaggingEditorActiveTooltip />;
}
